// Ported from cartadigital-pos app/utils/item-options-validation.ts — keep in
// behavioral parity with backend option validation.
import type { IMenuOption } from './pos-types';

export type ISelectedOptionsMap = Record<string, Record<string, number>>;

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/** i18n-ready validation error: consumers render via $t(key, params). */
export interface IOptionValidationError {
  key: string;
  params?: Record<string, unknown>;
}

export interface ISelectedOptionsValidationResult {
  ok: boolean;
  errorsByOptionId: Record<number, IOptionValidationError>;
  firstError?: IOptionValidationError;
  sanitized: ISelectedOptionsMap;
}

/**
 * Normalize potentially corrupted option values into a safe, non-negative, integer map.
 * - Drops non-finite / NaN values
 * - Clamps negatives to 0
 * - Truncates decimals (option quantities are counts)
 */
export const sanitizeSelectedOptions = (
  selectedOptionsInput: unknown
): ISelectedOptionsMap => {
  const out: ISelectedOptionsMap = {};

  if (!isPlainRecord(selectedOptionsInput)) return out;

  for (const [optionIdKey, innerRaw] of Object.entries(selectedOptionsInput)) {
    const optionId = Number(optionIdKey);
    if (!Number.isSafeInteger(optionId) || optionId <= 0) continue;

    if (!isPlainRecord(innerRaw)) continue;

    const optionKey = String(optionId);
    const innerOut = out[optionKey] ?? {};
    let hasAny = false;

    for (const [optionItemIdKey, rawVal] of Object.entries(innerRaw)) {
      const optionItemId = Number(optionItemIdKey);
      if (!Number.isSafeInteger(optionItemId) || optionItemId <= 0) continue;

      if (typeof rawVal !== 'number' || !Number.isFinite(rawVal)) continue;

      const val = Math.max(0, Math.trunc(rawVal));
      if (val <= 0) continue;

      const optionItemKey = String(optionItemId);
      innerOut[optionItemKey] = Math.max(innerOut[optionItemKey] ?? 0, val);
      hasAny = true;
    }

    if (hasAny) {
      out[optionKey] = innerOut;
    }
  }

  return out;
};

export interface IHydratedStoredOptions {
  hydrated: ISelectedOptionsMap;
  /** True when any stored group/item id no longer exists in the catalog. */
  droppedAny: boolean;
}

/**
 * Hydrates stored selections (e.g. a cart line being edited) against the live
 * catalog: unknown option GROUPS and unknown option ITEMS inside known groups
 * are both dropped. `validateSelectedOptions().sanitized` is NOT sufficient
 * for this — it keeps unknown item ids inside known groups (raising
 * errOptInvalid), and those ghosts inflate the selected count so a maxed
 * `multiple` group refuses every new selection, which reads as "read-only".
 * Callers should warn the user when `droppedAny` is true.
 */
export const hydrateStoredSelectedOptions = (
  itemOptions: IMenuOption[],
  storedInput: unknown
): IHydratedStoredOptions => {
  const sanitizedBase = sanitizeSelectedOptions(storedInput);
  const hydrated: ISelectedOptionsMap = {};
  let droppedAny = false;

  const groupsById = new Map(itemOptions.map((option) => [option.id, option]));

  for (const [optionIdKey, storedItems] of Object.entries(sanitizedBase)) {
    const group = groupsById.get(Number(optionIdKey));
    if (!group) {
      droppedAny = true;
      continue;
    }

    const knownItemIds = new Set(group.items.map((it) => it.id));
    const inner: Record<string, number> = {};
    let hasAny = false;

    for (const [optionItemIdKey, val] of Object.entries(storedItems)) {
      if (!knownItemIds.has(Number(optionItemIdKey))) {
        droppedAny = true;
        continue;
      }
      inner[optionItemIdKey] = val;
      hasAny = true;
    }

    if (hasAny) {
      hydrated[optionIdKey] = inner;
    }
  }

  return { hydrated, droppedAny };
};

export const validateSelectedOptions = (
  itemOptions: IMenuOption[],
  selectedOptionsInput: unknown
): ISelectedOptionsValidationResult => {
  const sanitizedBase = sanitizeSelectedOptions(selectedOptionsInput);

  const errorsByOptionId: Record<number, IOptionValidationError> = {};
  let firstError: IOptionValidationError | undefined;

  const setError = (optionId: number, error: IOptionValidationError) => {
    if (!errorsByOptionId[optionId]) {
      errorsByOptionId[optionId] = error;
    }
    if (!firstError) firstError = error;
  };

  const pickNonMultipleSelection = (params: {
    option: IMenuOption;
    selectedItems: Record<string, number>;
  }): Record<string, number> => {
    const { option, selectedItems } = params;

    const candidates = Object.entries(selectedItems)
      .filter(([, v]) => typeof v === 'number' && Number.isFinite(v) && v > 0)
      .map(([k]) => Number(k))
      .filter((n) => Number.isSafeInteger(n) && n > 0);

    if (candidates.length === 0) return {};

    const byId = new Map<number, { index: number }>();
    for (const it of option.items) {
      const idx = Number.isFinite(Number(it.index)) ? Number(it.index) : 9999;
      byId.set(it.id, { index: idx });
    }

    const valid = candidates.filter((id) => byId.has(id));
    if (valid.length === 0) {
      return {};
    }

    valid.sort((a, b) => {
      const ia = byId.get(a)?.index ?? 9999;
      const ib = byId.get(b)?.index ?? 9999;
      if (ia !== ib) return ia - ib;
      return a - b;
    });

    const chosen = valid[0];
    return chosen ? { [String(chosen)]: 1 } : {};
  };

  // Filter to only known option IDs, and apply semantic normalization per option constraints.
  const sanitized: ISelectedOptionsMap = {};

  for (const option of itemOptions) {
    const optionId = option.id;
    const selectedItems: Record<string, number> =
      sanitizedBase[String(optionId)] ?? {};

    // Enforce non-multiple semantics:
    // - At most one selected option item key
    // - Value is a boolean-like selection (0/1)
    if (!option.multiple) {
      // Healing: if we ever get multiple selections in a non-multiple group
      // (e.g., corrupted state / legacy payload), we deterministically keep one
      // selection without blocking the user.
      const healed = pickNonMultipleSelection({
        option,
        selectedItems,
      });
      if (Object.keys(healed).length > 0) {
        sanitized[String(optionId)] = healed;
      }
    } else {
      // Multiple option groups keep quantities as-is (already sanitized to positive ints).
      if (Object.keys(selectedItems).length > 0) {
        sanitized[String(optionId)] = { ...selectedItems };
      }
    }

    const normalizedItems = sanitized[String(optionId)] || {};

    const values = Object.values(normalizedItems).filter(
      (v) => typeof v === 'number' && Number.isFinite(v) && v > 0
    );
    const sum = values.reduce((acc, val) => acc + val, 0);

    const minRequiredBase = option.mandatory
      ? Math.max(option.min, 1)
      : option.min;
    // Backend parity: when an option group represents variants (`main=true`),
    // the user must pick exactly one even if `mandatory/min` are unset.
    const minRequired = option.main
      ? Math.max(minRequiredBase, 1)
      : minRequiredBase;

    if (minRequired > 0 && sum < minRequired) {
      setError(
        optionId,
        option.main && minRequired === 1
          ? { key: 'posV2.errOptVariant' }
          : minRequired === 1
          ? { key: 'posV2.errOptMinOne' }
          : { key: 'posV2.errOptMin', params: { min: minRequired } }
      );
      // Keep checking to provide best effort diagnostics for corrupted states.
    }

    if (option.multiple && option.max > 0 && sum > option.max) {
      setError(optionId, {
        key: 'posV2.errOptMax',
        params: { max: option.max },
      });
    }

    // Validate selected option items exist and respect per-item max.
    for (const [optionItemIdKey, rawVal] of Object.entries(normalizedItems)) {
      const optionItemId = Number(optionItemIdKey);
      if (!Number.isSafeInteger(optionItemId) || optionItemId <= 0) continue;

      const val =
        typeof rawVal === 'number' && Number.isFinite(rawVal) ? rawVal : 0;
      if (val <= 0) continue;

      const optionItem = option.items.find((it) => it.id === optionItemId);
      if (!optionItem) {
        setError(optionId, { key: 'posV2.errOptInvalid' });
        continue;
      }

      if (optionItem.max > 0 && val > optionItem.max) {
        setError(optionId, {
          key: 'posV2.errOptItemMax',
          params: { max: optionItem.max, title: optionItem.title },
        });
      }
    }
  }

  return {
    ok: Object.keys(errorsByOptionId).length === 0,
    errorsByOptionId,
    firstError,
    sanitized,
  };
};
